import { useCallback } from 'react';
import {
  QueryKey,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useInfiniteQuery as useInfiniteQueryInternal,
  QueryFunction,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useDefaultRetryPolicy } from '@antimatterhq/ui/hooks/react-query/useDefaultRetryPolicy';
import { useDefaultRequestErrorHandling } from '@antimatterhq/ui/hooks/react-query/useDefaultRequestErrorHandling';
import { useDefaultRequestSuccessHandling } from '@antimatterhq/ui/hooks/react-query/useDefaultRequestSuccessHandling';

export type { InfiniteData } from '@tanstack/react-query';

/**
 * @description A wrapper around `@tanstack/react-query`'s `useInfiniteQuery` hook.
 * Provides enhancements:
 * 1.  **Automatic 401 Retry**: Automatically attempts to refresh the authentication token on a 401 error
 *     using `useDefaultRequestErrorHandling` and retries the query *once* if the refresh is successful.
 *     This retry is managed internally within the `queryFn` wrapper.
 * 2.  **Default Retry Policy**: Applies a default retry strategy for *non-401* errors (e.g., network issues)
 *     using `useDefaultRetryPolicy`, unless a specific `retry` option is provided to the hook.
 * 3.  **Default Options**: Sets default values for `refetchOnWindowFocus` (false) and `refetchOnMount` (false)
 *     unless overridden in options.
 *
 * @template TQueryFnData - Type of data returned by the query function per page.
 * @template TError - Type of error thrown by the query function. Defaults to `AxiosError`.
 * @template TData - Type of data structure containing all pages (usually `InfiniteData<TQueryFnData>`).
 * @template TQueryKey - Type of the query key.
 * @template TPageParam - Type of the page parameter used for fetching pages.
 *
 * @param {UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey, TPageParam>} options - Original options passed to `useInfiniteQuery`.
 * The `retry` option configures the *non-401* retry behavior.
 * @returns {UseInfiniteQueryResult<TData, TError>} The result object from `useInfiniteQuery`.
 */
export function useInfiniteQuery<
  TQueryFnData = unknown,
  TError = AxiosError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: UseInfiniteQueryOptions<
    TQueryFnData,
    TError,
    TData,
    TQueryFnData,
    TQueryKey,
    TPageParam
  >,
): UseInfiniteQueryResult<TData, TError> {
  const shouldRetry = useDefaultRetryPolicy();
  const handleError = useDefaultRequestErrorHandling();
  const handleSuccess = useDefaultRequestSuccessHandling();

  const queryFn: QueryFunction<TQueryFnData, TQueryKey, TPageParam> =
    useCallback(
      async (...args) => {
        try {
          const result = await (
            options.queryFn as QueryFunction<
              TQueryFnData,
              TQueryKey,
              TPageParam
            >
          )?.(...args);
          await handleSuccess();
          return result;
        } catch (error) {
          if (await handleError(error)) {
            // After error handling, try again in case the error was due to a 401.
            // If we made it this far, the token refresh worked and we can try again.
            // No try/catch here, we want to throw the error if it's not handled.
            const result = await (
              options.queryFn as QueryFunction<
                TQueryFnData,
                TQueryKey,
                TPageParam
              >
            )?.(...args);

            await handleSuccess();
            return result;
          }

          throw error;
        }
      },
      [options.queryFn, handleError, handleSuccess],
    );

  return useInfiniteQueryInternal({
    ...options,
    queryFn,
    refetchOnWindowFocus: options.refetchOnWindowFocus || false,
    refetchOnMount: options.refetchOnMount || false,
    retry: options.retry ?? shouldRetry,
    throwOnError: options.throwOnError ?? false,
  });
}
